import React, { useState, useEffect } from 'react';
import './ThermalRunawayWarning.css';

const ThermalRunawayWarning = ({ batteryData, isVisible }) => {
  const [traStatus, setTraStatus] = useState('GREEN'); // GREEN, ORANGE, RED
  const [anomalies, setAnomalies] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Thermal Runaway Analysis based on requirements document
  useEffect(() => {
    if (!batteryData) return;

    const {cells, temperature, voltage} = batteryData;
    const detected = [];

    // Check 1: Cell voltage anomalies (Level 3 fault - like Cell 180 at 2.902V vs 3.28V)
    if (cells?.voltages) {
      const avgVoltage = cells.avgVoltage || cells.voltages.reduce((a, b) => a + b, 0) / cells.voltages.length;
      const cellMaxVoltageLimit = 3.70; // Increased from 3.65V to prevent false positives
      const cellMinOperationVolt = 2.80; // From requirements: CellMinOperationVolt_C

      cells.voltages.forEach((voltage, idx) => {
        if (voltage > cellMaxVoltageLimit) {
          detected.push({
            type: 'OVERVOLTAGE',
            cell: idx + 1,
            value: voltage,
            threshold: cellMaxVoltageLimit,
            severity: 'CRITICAL'
          });
        } else if (voltage < cellMinOperationVolt) {
          detected.push({
            type: 'UNDERVOLTAGE',
            cell: idx + 1,
            value: voltage,
            threshold: cellMinOperationVolt,
            severity: 'CRITICAL'
          });
        } else if (Math.abs(voltage - avgVoltage) > 0.15) {
          detected.push({
            type: 'IMBALANCE',
            cell: idx + 1,
            value: voltage,
            deviation: Math.abs(voltage - avgVoltage),
            severity: 'WARNING'
          });
        }
      });
    }

    // Check 2: Temperature anomalies
    if (temperature?.pack) {
      if (temperature.pack > 45) {
        detected.push({
          type: 'HIGH_TEMP',
          value: temperature.pack,
          threshold: 45,
          severity: 'CRITICAL'
        });
      } else if (temperature.pack > 38) {
        detected.push({
          type: 'ELEVATED_TEMP',
          value: temperature.pack,
          threshold: 38,
          severity: 'WARNING'
        });
      }
    }

    // Check 3: Cell temperature variance
    if (temperature?.cells) {
      const maxCellTemp = Math.max(...temperature.cells);
      const minCellTemp = Math.min(...temperature.cells);
      const tempVariance = maxCellTemp - minCellTemp;

      if (tempVariance > 8) {
        detected.push({
          type: 'TEMP_VARIANCE',
          value: tempVariance,
          max: maxCellTemp,
          min: minCellTemp,
          severity: 'WARNING'
        });
      }
    }

    setAnomalies(detected);

    // Determine TRA status based on ML classification (simulated)
    const criticalCount = detected.filter(a => a.severity === 'CRITICAL').length;
    const warningCount = detected.filter(a => a.severity === 'WARNING').length;

    if (criticalCount > 0) {
      setTraStatus('RED');
    } else if (warningCount > 2) {
      setTraStatus('ORANGE');
    } else {
      setTraStatus('GREEN');
    }
  }, [batteryData]);

  if (!isVisible) return null;

  const getStatusConfig = () => {
    switch (traStatus) {
      case 'RED':
        return {
          label: 'CRITICAL - THERMAL RUNAWAY IMMINENT',
          icon: '🚨',
          color: '#ef4444',
          action: 'EMERGENCY SHUTDOWN REQUIRED',
          bgClass: 'status-red'
        };
      case 'ORANGE':
        return {
          label: 'WARNING - INCREASED MONITORING',
          icon: '⚠️',
          color: '#f59e0b',
          action: 'Continuous Monitoring Active',
          bgClass: 'status-orange'
        };
      case 'GREEN':
      default:
        return {
          label: 'NORMAL OPERATION',
          icon: '✓',
          color: '#10b981',
          action: 'All Systems Nominal',
          bgClass: 'status-green'
        };
    }
  };

  const status = getStatusConfig();

  // Auto-collapse GREEN status after 10s
  useEffect(() => {
    if (traStatus === 'GREEN') {
      const timer = setTimeout(() => setIsCollapsed(true), 10000);
      return () => clearTimeout(timer);
    } else {
      setIsCollapsed(false); // Always expand for warnings/critical
    }
  }, [traStatus]);

  return (
    <div
      className={`tra-warning-container ${status.bgClass} ${isCollapsed ? 'collapsed' : ''}`}
      onClick={() => setIsCollapsed(!isCollapsed)}
    >
      <div className="tra-header">
        <div className="tra-status-badge" style={{ borderColor: status.color }}>
          <span className="tra-icon">{status.icon}</span>
          <div>
            <div className="tra-label">THERMAL RUNAWAY ANALYSIS</div>
            <div className="tra-status-text" style={{ color: status.color }}>
              {status.label}
            </div>
          </div>
        </div>
        <div className="tra-algorithm-badge">
          <span className="algorithm-label">LSTM ML Model</span>
          <span className="algorithm-status">● Active</span>
        </div>
        <button
          className="tra-collapse-btn"
          onClick={(e) => { e.stopPropagation(); setIsCollapsed(!isCollapsed); }}
        >
          {isCollapsed ? '▼' : '▲'}
        </button>
      </div>

      <div className="tra-metrics">
        <div className="tra-metric">
          <span className="metric-label">Pack Temperature</span>
          <span className="metric-value" style={{ color: batteryData?.temperature?.pack > 38 ? '#f59e0b' : '#10b981' }}>
            {batteryData?.temperature?.pack?.toFixed(1) || 0}°C
          </span>
        </div>
        <div className="tra-metric">
          <span className="metric-label">Cell Voltage Range</span>
          <span className="metric-value">
            {batteryData?.cells?.minVoltage?.toFixed(3) || 0}V - {batteryData?.cells?.maxVoltage?.toFixed(3) || 0}V
          </span>
        </div>
        <div className="tra-metric">
          <span className="metric-label">Anomalies Detected</span>
          <span className="metric-value" style={{ color: anomalies.length > 0 ? '#f59e0b' : '#10b981' }}>
            {anomalies.length}
          </span>
        </div>
      </div>

      {anomalies.length > 0 && (
        <div className="tra-anomalies">
          <h4>Detected Anomalies</h4>
          <div className="anomalies-list">
            {anomalies.slice(0, 5).map((anomaly, idx) => (
              <div key={idx} className={`anomaly-item severity-${anomaly.severity.toLowerCase()}`}>
                <span className="anomaly-type">{anomaly.type.replace(/_/g, ' ')}</span>
                {anomaly.cell && <span className="anomaly-cell">Cell #{anomaly.cell}</span>}
                <span className="anomaly-value">
                  {anomaly.value?.toFixed(3) || anomaly.deviation?.toFixed(3)}
                  {anomaly.type.includes('VOLTAGE') ? 'V' : anomaly.type.includes('TEMP') ? '°C' : ''}
                </span>
              </div>
            ))}
            {anomalies.length > 5 && (
              <div className="anomaly-more">+{anomalies.length - 5} more anomalies</div>
            )}
          </div>
        </div>
      )}

      <div className="tra-action" style={{ borderLeftColor: status.color }}>
        {status.action}
      </div>
    </div>
  );
};

export default ThermalRunawayWarning;
